/**
 * AreaControlGrid — pitch grid overlay showing which team controlled each
 * zone of the field, distinct from the per-player density heat maps.
 *
 * Each of the shared 6x4 zones (see heatMapZones.ts) is tinted with the
 * controlling team's color, or a neutral slate tone when contested.
 */
import React from "react";
import {
  ZONES,
  PITCH_WIDTH,
  PITCH_HEIGHT,
  ZONE_COLS,
  ZONE_W,
  type AreaControlData,
} from "../../utils/heatMapZones";
import {
  PENALTY_ARC_Y_OFFSET,
  PITCH_MARKINGS,
  PITCH_MARKINGS_DERIVED,
} from "../../utils/pitchGeometry";
import TeamLogo, { type TeamLogoProps } from "../../../../components/TeamLogo";
import AttackDirectionCaption from "../atoms/AttackDirectionCaption";

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface AreaControlGridProps {
  data: AreaControlData;
  homeTeamName: string;
  awayTeamName: string;
  homeColor: string;
  awayColor: string;
  /**
   * Team objects (PMA-COCK-161) used to render a small crest between the
   * swatch and the label of each team's legend row. Optional; omitting
   * either/both reproduces today's exact legend. Contested row is
   * unaffected — it has no team.
   */
  homeTeam?: TeamLogoProps["team"];
  awayTeam?: TeamLogoProps["team"];
  t: (key: string, fallback?: string) => string;
  "data-testid"?: string;
}

/* ------------------------------------------------------------------ */
/*  Pitch markup constants (all in StatsBomb coordinate units)         */
/* ------------------------------------------------------------------ */

const PA_WIDTH = PITCH_MARKINGS.penaltyAreaDepth;
const PA_HEIGHT = PITCH_MARKINGS.penaltyAreaHeight;
const PA_Y = PITCH_MARKINGS_DERIVED.penaltyAreaY;

const GA_WIDTH = PITCH_MARKINGS.goalAreaDepth;
const GA_HEIGHT = PITCH_MARKINGS.goalAreaHeight;
const GA_Y = PITCH_MARKINGS_DERIVED.goalAreaY;

const CC_RX = PITCH_MARKINGS.centerCircleRx;
const CC_RY = PITCH_MARKINGS.centerCircleRy;
const PEN_X = PITCH_MARKINGS.penaltyMarkX;
const PEN_R = PITCH_MARKINGS.penaltyMarkRadius;
const CORNER_R = PITCH_MARKINGS.cornerArcR;

const CONTESTED_COLOR = "#64748b"; // slate-500

/** Convert a hex color + alpha into an rgba() string; falls back to the contested tone. */
function hexToRgba(hex: string, alpha: number): string {
  const m = /^#([0-9A-Fa-f]{6})$/.exec(hex.trim());
  if (!m) return `rgba(100, 116, 139, ${alpha})`;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const AreaControlGrid: React.FC<AreaControlGridProps> = ({
  data,
  homeTeamName,
  awayTeamName,
  homeColor,
  awayColor,
  homeTeam,
  awayTeam,
  t,
  "data-testid": testId,
}) => {
  const cellByZoneId = new Map(data.cells.map((c) => [c.zoneId, c]));

  return (
    <div className="flex flex-col items-center gap-2" data-testid={testId}>
      <div
        className="relative w-full max-w-2xl rounded-lg border border-slate-700 bg-green-800 overflow-hidden"
        style={{
          aspectRatio: `${PITCH_WIDTH + 4} / ${PITCH_HEIGHT + 4}`,
        }}
      >
        <svg
          viewBox={`-2 -2 ${PITCH_WIDTH + 4} ${PITCH_HEIGHT + 4}`}
          className="absolute inset-0 w-full h-full"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={t("analytics.areaControl", "Area Control")}
          data-testid={testId ? `${testId}-svg` : undefined}
        >
          <title>{t("analytics.areaControl", "Area Control")}</title>

          {/* ─── Field background ─── */}
          <rect
            x={0}
            y={0}
            width={PITCH_WIDTH}
            height={PITCH_HEIGHT}
            fill="#2d6a2e"
            rx={1}
          />

          {/* ─── Mowing stripes (alternating vertical bands) ─── */}
          {Array.from({ length: ZONE_COLS }, (_, i) =>
            i % 2 === 0 ? (
              <rect
                key={`stripe-${i}`}
                x={i * ZONE_W}
                y={0}
                width={ZONE_W}
                height={PITCH_HEIGHT}
                fill="rgba(255,255,255,0.03)"
              />
            ) : null,
          )}

          {/* ─── Control zone tint ─── */}
          {ZONES.map((zone) => {
            const cell = cellByZoneId.get(zone.id);
            const owner = cell?.owner ?? "contested";
            // Home/away fills match the legend swatch's 0.85 alpha (and
            // Momentum/Recovery's full-strength color) — at the previous 0.55
            // alpha, red hues composited over the green pitch background read
            // as muddy orange/brown (e.g. #dc2626 over #2d6a2e -> ~#8d452a),
            // even though the underlying hex was identical to Momentum's.
            const fill =
              owner === "home"
                ? hexToRgba(homeColor, 0.85)
                : owner === "away"
                  ? hexToRgba(awayColor, 0.85)
                  : hexToRgba(CONTESTED_COLOR, 0.3);

            return (
              <rect
                key={`cell-${zone.id}`}
                x={zone.x0}
                y={zone.y0}
                width={zone.x1 - zone.x0}
                height={zone.y1 - zone.y0}
                fill={fill}
                stroke="rgba(15, 23, 42, 0.35)"
                strokeWidth={0.25}
                data-testid={testId ? `${testId}-zone-${zone.id}` : undefined}
                data-zone-id={zone.id}
                data-zone-owner={owner}
              />
            );
          })}

          {/* ─── Pitch markings ─── */}
          <g stroke="rgba(255,255,255,0.55)" strokeWidth={0.4} fill="none">
            <rect
              x={0}
              y={0}
              width={PITCH_WIDTH}
              height={PITCH_HEIGHT}
              rx={0.5}
            />
            <line
              x1={PITCH_WIDTH / 2}
              y1={0}
              x2={PITCH_WIDTH / 2}
              y2={PITCH_HEIGHT}
            />
            <ellipse
              cx={PITCH_WIDTH / 2}
              cy={PITCH_HEIGHT / 2}
              rx={CC_RX}
              ry={CC_RY}
            />
            <circle
              cx={PITCH_WIDTH / 2}
              cy={PITCH_HEIGHT / 2}
              r={PEN_R}
              fill="rgba(255,255,255,0.55)"
            />
            <rect x={0} y={PA_Y} width={PA_WIDTH} height={PA_HEIGHT} />
            <rect
              x={PITCH_WIDTH - PA_WIDTH}
              y={PA_Y}
              width={PA_WIDTH}
              height={PA_HEIGHT}
            />
            <rect x={0} y={GA_Y} width={GA_WIDTH} height={GA_HEIGHT} />
            <rect
              x={PITCH_WIDTH - GA_WIDTH}
              y={GA_Y}
              width={GA_WIDTH}
              height={GA_HEIGHT}
            />
            <circle
              cx={PEN_X}
              cy={PITCH_HEIGHT / 2}
              r={PEN_R}
              fill="rgba(255,255,255,0.55)"
            />
            <circle
              cx={PITCH_WIDTH - PEN_X}
              cy={PITCH_HEIGHT / 2}
              r={PEN_R}
              fill="rgba(255,255,255,0.55)"
            />
            <path
              d={`M ${PA_WIDTH} ${
                PITCH_HEIGHT / 2 - PENALTY_ARC_Y_OFFSET
              } A ${CC_RX} ${CC_RY} 0 0 1 ${PA_WIDTH} ${
                PITCH_HEIGHT / 2 + PENALTY_ARC_Y_OFFSET
              }`}
            />
            <path
              d={`M ${PITCH_WIDTH - PA_WIDTH} ${
                PITCH_HEIGHT / 2 - PENALTY_ARC_Y_OFFSET
              } A ${CC_RX} ${CC_RY} 0 0 0 ${PITCH_WIDTH - PA_WIDTH} ${
                PITCH_HEIGHT / 2 + PENALTY_ARC_Y_OFFSET
              }`}
            />
            <path
              d={`M 0 ${CORNER_R} A ${CORNER_R} ${CORNER_R} 0 0 1 ${CORNER_R} 0`}
            />
            <path
              d={`M ${
                PITCH_WIDTH - CORNER_R
              } 0 A ${CORNER_R} ${CORNER_R} 0 0 1 ${PITCH_WIDTH} ${CORNER_R}`}
            />
            <path
              d={`M 0 ${
                PITCH_HEIGHT - CORNER_R
              } A ${CORNER_R} ${CORNER_R} 0 0 0 ${CORNER_R} ${PITCH_HEIGHT}`}
            />
            <path
              d={`M ${
                PITCH_WIDTH - CORNER_R
              } ${PITCH_HEIGHT} A ${CORNER_R} ${CORNER_R} 0 0 0 ${PITCH_WIDTH} ${
                PITCH_HEIGHT - CORNER_R
              }`}
            />
          </g>
        </svg>
      </div>

      {/* PMA-COCK-196: "Dirección de ataque" caption for BOTH sides — this
          map combines both teams on one pitch (unlike `TeamControlMap`,
          which renders one card per team), so it shows both directions at
          once rather than picking one. `homeTeamName`/`awayTeamName` are
          required props, so this always has a side to render for; no
          caller change needed.
          Judge round-1 point 13 fix: two opposite arrows next to the same
          generic label, with no team identity, read as self-contradictory
          on this single combined pitch — the opposite of the client's own
          reason for asking. Each caption now also names its team via the
          `teamName` prop, using the same `homeTeamName`/`awayTeamName` this
          component already receives. */}
      <div
        className="flex flex-wrap items-center justify-center gap-4"
        data-testid={testId ? `${testId}-attack-directions` : undefined}
      >
        <AttackDirectionCaption
          side="home"
          t={t}
          teamName={homeTeamName}
          data-testid={testId ? `${testId}-home` : undefined}
        />
        <AttackDirectionCaption
          side="away"
          t={t}
          teamName={awayTeamName}
          data-testid={testId ? `${testId}-away` : undefined}
        />
      </div>

      {/* ─── Legend ───
          PMA-COCK-170 (client wave 2026-09-07): "revisar porque sale un
          poco cortado" — bumped text-xs -> text-sm and given its own
          `-legend` testid so MatchAnalytics.tsx's PDF export capture
          (`captureSelectorAsImage`) always un-truncates it, on top of the
          pre-existing `flex-wrap` (kept) and no `truncate` class (never
          added here). */}
      <div
        className="flex flex-wrap items-center justify-center gap-4 text-base text-slate-200"
        data-testid={testId ? `${testId}-legend` : undefined}
      >
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ backgroundColor: hexToRgba(homeColor, 0.85) }}
          />
          {homeTeam && (
            <TeamLogo
              team={homeTeam}
              bare
              size="xs"
              testId={testId ? `${testId}-home-crest` : undefined}
            />
          )}
          <span>
            {t("analytics.homeControlled", "{{team}} controlled").replace(
              "{{team}}",
              homeTeamName,
            )}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ backgroundColor: hexToRgba(awayColor, 0.85) }}
          />
          {awayTeam && (
            <TeamLogo
              team={awayTeam}
              bare
              size="xs"
              testId={testId ? `${testId}-away-crest` : undefined}
            />
          )}
          <span>
            {t("analytics.awayControlled", "{{team}} controlled").replace(
              "{{team}}",
              awayTeamName,
            )}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ backgroundColor: hexToRgba(CONTESTED_COLOR, 0.6) }}
          />
          <span>{t("analytics.contested", "Contested")}</span>
        </div>
      </div>
    </div>
  );
};

export default AreaControlGrid;
